import { useCallback, useEffect, useMemo } from "react";
import { Handle, Position, NodeProps, Node, NodeToolbar, useUpdateNodeInternals } from "@xyflow/react";
import { KalikoNodeStatus, KalikoNodeType } from "../../../../Core/Api/Types/kaliko";
import { NodeTypeSvg } from "./node-picker-panel";
import { Box, Button, Stack, SxProps, Typography, useTheme } from "@mui/material";
import { DeleteForeverOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { DeleteDiagramNodeById, GetOrientation } from "../../../../Redux/Reducers/redux-slice-workflow-designer";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
export type KalikoNodeProps = Node<any, "kalikoNode">;

export function KalikoNode(props: NodeProps<KalikoNodeProps>) {
  const dispatch = useDispatch();
  const { data, positionAbsoluteX, id, selected } = props;

  const nodeType = data.type;
  const status = data.status;
  const name = data.name;

  const orientation = useSelector(GetOrientation);
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    updateNodeInternals(id);
  }, [orientation]);
  const handleDeleteClick = () => {
    dispatch(DeleteDiagramNodeById(id));
  };
  const { t } = useTranslation();
  const theme = useTheme();

  const isCardNode = useMemo(
    () =>
      ![
        KalikoNodeType.GatewayExclusive,
        KalikoNodeType.GatewayInclusive,
        KalikoNodeType.GatewayParallel,
        KalikoNodeType.StartEvent,
        KalikoNodeType.EndEvent,
      ].includes(nodeType),
    [nodeType]
  );

  const getBorder = useCallback(
    (selected: boolean, status: KalikoNodeStatus, isCardNode: boolean) => {
      if (selected) return `1px solid ${theme.palette.primary.main}`;
      if (status === KalikoNodeStatus.Completed) return `1px solid ${theme.palette.success.main}`;
      if (status === KalikoNodeStatus.Cancelled) return `1px solid ${theme.palette.warning.main}`;
      if (status === KalikoNodeStatus.Failed) return `1px solid ${theme.palette.error.main}`;
      if (status === KalikoNodeStatus.Running) return `1px dashed ${theme.palette.info.main}`;
      if (status === KalikoNodeStatus.Skipped) return `1px solid ${theme.palette.text.tier}`;

      if (isCardNode) return `1px solid ${theme.palette.background.inverted}`;
      return "";
    },
    [theme?.palette.mode]
  );

  const getBackground = useCallback(
    (status: KalikoNodeStatus) => {
      if (status === KalikoNodeStatus.Completed) return theme.palette.success.alphaHover;
      if (status === KalikoNodeStatus.Cancelled) return theme.palette.warning.alphaHover;
      if (status === KalikoNodeStatus.Failed) return theme.palette.error.alphaHover;
      if (status === KalikoNodeStatus.Running) return theme.palette.info.alphaHover;
      return theme.palette.background.paper;
    },
    [theme?.palette.mode]
  );

  const nodeStyle: SxProps = useMemo(
    () => ({
      border: getBorder(selected ?? false, status, isCardNode),
      backgroundColor: getBackground(status),
      borderRadius: isCardNode ? "12px" : "100%",
      height: isCardNode ? "auto" : "38px",
      maxWidth: "200px",
    }),
    [selected, status, isCardNode, getBorder, getBackground]
  );

  return (
    <Box sx={nodeStyle}>
      <NodeToolbar
        isVisible={selected}
        position={orientation === "horizontal" ? Position.Top : positionAbsoluteX > 0 ? Position.Right : Position.Left}
      >
        <Button onClick={handleDeleteClick} startIcon={<DeleteForeverOutlined />}>
          {t("common:delete")}
        </Button>
      </NodeToolbar>
      {isCardNode ? (
        <Stack alignItems={"center"} direction={"row"} spacing={1} p={1}>
          <Typography
            textAlign={"center"}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
            fontWeight={500}
            variant="body1"
            color={"text.secondary"}
          >
            {name}
          </Typography>
          <NodeTypeSvg context="flow" type={nodeType} />
        </Stack>
      ) : (
        <NodeTypeSvg context="flow" type={nodeType} />
      )}
      {nodeType !== KalikoNodeType.StartEvent && (
        <Handle type="target" position={orientation === "horizontal" ? Position.Left : Position.Top} />
      )}
      {nodeType !== KalikoNodeType.EndEvent && (
        <Handle type="source" position={orientation === "horizontal" ? Position.Right : Position.Bottom} />
      )}
    </Box>
  );
}
